import { useState, useContext } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCashRegister } from '@fortawesome/pro-regular-svg-icons'
import { format } from 'date-fns'

import BrandingContext from '../../../context/BrandingContext'
import Button from '../../../components/Button'
import usePrivateApi from '../../../hooks/usePrivateApi'
import { useEffect } from 'react'

const CheckoutStatus = () => {
  const [stripeLink, setStripeLink] = useState(null)
  const [stripeLinkError, setStripeLinkError] = useState(null)
  const [branding, setBranding] = useContext(BrandingContext)

  const [{ res, success, error }, { sendRequest, resetSuccess, resetError }] =
    usePrivateApi()

  const handleCheckStatus = async () => {
    resetError()
    sendRequest({
      url: '/settings/checkout',
    })
  }

  useEffect(() => {
    if (branding.stripe.accountId && branding.stripe.requiresAction) {
      sendRequest({ url: '/settings/checkout/link' })
    }
  }, [sendRequest, branding])

  useEffect(() => {
    if (success && res.data.url) {
      setStripeLink(res.data.url)
    }

    if (success && res.data.stripe) {
      setBranding({
        ...branding,
        stripe: res.data.stripe,
      })
    }

    resetSuccess()
  }, [res, success, resetSuccess, branding, setBranding])

  useEffect(() => {
    if (error) {
      setStripeLinkError(true)
      resetError()
    }
  }, [error, resetError])

  if (
    branding.stripe.accountId &&
    branding.stripe.requiresAction &&
    !stripeLink &&
    !stripeLinkError
  ) {
    return (
      <div className="px-6 pt-8 pb-12 space-y-4 sm:space-y-0 sm:flex sm:space-x-8 sm:items-start">
        <div className="flex items-center justify-center p-12 flex-1">
          <span className="spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="border-b border-gray-200 px-6 pt-8 pb-12 space-y-4 sm:space-y-0 sm:flex sm:space-x-8 sm:items-start">
      <div className="max-w-sm flex-1">
        <h2 className="text-lg font-semibold tracking-tight mb-2">
          Dealer checkout
        </h2>
        <div className="text-gray-600 text-sm space-y-2 max-w-prose">
          <p>
            Dealer checkout enables your subdealers to purchase credits from you
            and for payment to taken automatically.
          </p>
          <p>
            Any credits purchased by a subdealer will be taken from your credit
            balance. If a subdealer wishes to purchase more credits than your
            current credit balance, you will need to purchase a credit package
            to facilite the transfer prior to the subdealer being able to
            checkout.
          </p>
          <p>
            To enable dealer checkout Top Gear Tuning will first need to enable
            the feature for you and begin the process of creating your Stripe
            account. Once done, you will be required to complete the Stripe set
            up process using the button in this portal.
          </p>
          <p>
            The Stripe account will be entirely managed by you. You will be
            required to go through the Stripe onboarding process which may
            include verifying your identification.
          </p>
          <p>
            Once complete, you will have access to the Stripe Dashboard to
            manage charges, refunds, payouts and much more. Top Gear Tuning
            cannot manage this Stripe account for you.
          </p>
        </div>
      </div>
      <div className="flex-1">
        {!branding.stripe.accountId && (
          <div className="bg-gray-50 rounded-md px-8 py-6">
            <FontAwesomeIcon
              icon={faCashRegister}
              className={`text-${branding.primaryColorPalette}-600`}
              size="lg"
            />
            <h5 className="font-medium mt-2 mb-1">Feature not enabled</h5>
            <p className="text-gray-600 text-sm mb-4">
              Top Gear Tuning has not enabled the dealer checkout feature on
              your account. If you would like access to this feature please
              create a support ticket.
            </p>
          </div>
        )}
        {branding.stripe.accountId && branding.stripe.requiresAction && (
          <div className="bg-gray-50 rounded-md px-8 py-6">
            <FontAwesomeIcon
              icon={faCashRegister}
              className={`text-${branding.primaryColorPalette}-600`}
              size="lg"
            />
            <h5 className="font-medium mt-2 mb-1">Checkout pending set up</h5>
            <p className="text-gray-600 text-sm mb-4">
              Dealer checkout has been enabled on your account but your Stripe
              account requires action.
            </p>
            <div className="space-x-2">
              <Button as="a" color="primary" href={stripeLink} target="_blank">
                Manage Stripe
              </Button>
              <Button color="white" onClick={handleCheckStatus}>
                Check status
              </Button>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Last checked:{' '}
              {
                //prettier-ignore
                branding.stripe.lastStatusUpdate
                  ? format(
                    new Date(branding.stripe.lastStatusUpdate),
                    "dd MMM yyyy 'at' h:mma"
                  )
                  : 'Never'
              }
            </p>
            <p className="text-xs text-gray-600 mt-4">
              Please note Stripe may review the information you submit before
              activating your account. Check back here to confirm once your
              account is active.
            </p>
          </div>
        )}
        {branding.stripe.accountId && !branding.stripe.requiresAction && (
          <div className="bg-gray-50 rounded-md px-8 py-6">
            <FontAwesomeIcon
              icon={faCashRegister}
              className={`text-${branding.primaryColorPalette}-600`}
              size="lg"
            />
            <h5 className="font-medium mt-2 mb-1">Checkout active</h5>
            <p className="text-gray-600 text-sm mb-4">
              Dealer checkout is active on your account. You can manage your
              default credit packages below. Alternatively you can set
              subdealer-level credit packages in the dealer settings directly.
            </p>
            <div className="space-x-2">
              <Button
                as="a"
                color="primary"
                href="https://dashboard.stripe.com"
                target="_blank"
              >
                Manage Stripe
              </Button>
              <Button color="white" onClick={handleCheckStatus}>
                Check status
              </Button>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Last checked:{' '}
              {
                //prettier-ignore
                branding.stripe.lastStatusUpdate
                  ? format(
                    new Date(branding.stripe.lastStatusUpdate),
                    "dd MMM yyyy 'at' h:mma"
                  )
                  : 'Never'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default CheckoutStatus
